import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Send, CheckCircle, XCircle } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

interface QuoteItem {
    description: string;
    quantity: number;
    unit_price: number;
    total: number;
}

const QuotesManagement: React.FC = () => {
    const [quotes, setQuotes] = useState<any[]>([]);
    const [opportunities, setOpportunities] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [newQuote, setNewQuote] = useState({
        opportunity_id: '', account_id: '', quote_number: `QT-${Date.now().toString().slice(-6)}`,
        subject: '', valid_until: '', terms: '', total_amount: 0,
        items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }] as QuoteItem[]
    });


    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [quotesRes, oppsRes, accsRes] = await Promise.all([
                apiFetch('/sales/quotes'),
                apiFetch('/crm/opportunities'),
                apiFetch('/accounts')
            ]);

            if (quotesRes.ok) setQuotes(await quotesRes.json());
            if (oppsRes.ok) setOpportunities(await oppsRes.json());
            if (accsRes.ok) setAccounts(await accsRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAddItem = () => {
        setNewQuote({
            ...newQuote,
            items: [...newQuote.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
        });
    };

    const updateItem = (index: number, field: keyof QuoteItem, value: any) => {
        setNewQuote(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index], [field]: value } as QuoteItem;

            if (field === 'quantity' || field === 'unit_price') {
                item.total = (item.quantity || 0) * (item.unit_price || 0);
            }

            updatedItems[index] = item;
            const totalAmount = updatedItems.reduce((sum, it) => sum + (it.total || 0), 0);
            return { ...prev, items: updatedItems, total_amount: totalAmount };
        });
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/sales/quotes', {
                method: 'POST',
                body: JSON.stringify(newQuote)
            });
            if (res.ok) {
                setShowCreateModal(false);
                setNewQuote({
                    opportunity_id: '', account_id: '', quote_number: `QT-${Date.now().toString().slice(-6)}`,
                    subject: '', valid_until: '', terms: '', total_amount: 0,
                    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }]
                });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const updateStatus = async (id: string, status: string) => {
        try {
            const res = await apiFetch(`/sales/quotes/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Quotes & Proposals</h2>
                    <p className="text-gray-500">Manage client quotations and sales documents</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition font-bold shadow-lg shadow-blue-200"
                >
                    <Plus size={20} /> Create Quote
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50/50">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Quote #</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Subject / Account</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Valid Until</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {quotes.map((q, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                                <td className="px-6 py-4 font-bold text-blue-600">{q.quote_number}</td>
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-800">{q.subject}</div>
                                    <div className="text-xs text-gray-400">{accounts.find(a => a.id === q.account_id)?.name}</div>
                                </td>
                                <td className="px-6 py-4 font-bold text-gray-800">${parseFloat(q.total_amount).toLocaleString()}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${q.status === 'Accepted' ? 'bg-green-100 text-green-700' :
                                        q.status === 'Draft' ? 'bg-gray-100 text-gray-600' :
                                            q.status === 'Sent' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                        {q.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">{new Date(q.valid_until).toLocaleDateString()}</td>
                                <td className="px-6 py-4 flex gap-4">
                                    {q.status === 'Draft' && (
                                        <button onClick={() => updateStatus(q.id, 'Sent')} className="text-blue-600 hover:text-blue-800"><Send size={18} /></button>
                                    )}
                                    {q.status === 'Sent' && (
                                        <>
                                            <button onClick={() => updateStatus(q.id, 'Accepted')} className="text-green-600 hover:text-green-800"><CheckCircle size={18} /></button>
                                            <button onClick={() => updateStatus(q.id, 'Rejected')} className="text-red-600 hover:text-red-800"><XCircle size={18} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <h3 className="text-2xl font-bold mb-6">Create New Quotation</h3>
                        <form onSubmit={handleCreate} className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Account *</label>
                                    <select className="w-full border p-3 rounded-xl" value={newQuote.account_id} onChange={e => setNewQuote({ ...newQuote, account_id: e.target.value })} required>
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Linked Opportunity</label>
                                    <select className="w-full border p-3 rounded-xl" value={newQuote.opportunity_id} onChange={e => setNewQuote({ ...newQuote, opportunity_id: e.target.value })}>
                                        <option value="">None</option>
                                        {opportunities.filter(o => o.account_id === newQuote.account_id).map(opp => <option key={opp.id} value={opp.id}>{opp.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Subject *</label>
                                    <input className="w-full border p-3 rounded-xl" value={newQuote.subject} onChange={e => setNewQuote({ ...newQuote, subject: e.target.value })} required placeholder="e.g. Website Redesign Proposal" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Quote Number</label>
                                    <input className="w-full border p-3 rounded-xl bg-gray-50" value={newQuote.quote_number} readOnly />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Valid Until *</label>
                                    <input type="date" className="w-full border p-3 rounded-xl" value={newQuote.valid_until} onChange={e => setNewQuote({ ...newQuote, valid_until: e.target.value })} required />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="font-bold text-lg flex items-center justify-between">
                                    Line Items
                                    <button type="button" onClick={handleAddItem} className="text-blue-600 text-sm flex items-center gap-1"><Plus size={16} /> Add Item</button>
                                </h4>
                                {newQuote.items.map((item, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-4 items-end bg-gray-50 p-4 rounded-2xl">
                                        <div className="col-span-6">
                                            <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                                            <input className="w-full border-b bg-transparent py-1" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} required />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-[10px] uppercase font-bold text-gray-400">Qty</label>
                                            <input type="number" className="w-full border-b bg-transparent py-1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value))} required />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-[10px] uppercase font-bold text-gray-400">Price</label>
                                            <input type="number" className="w-full border-b bg-transparent py-1" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value))} required />
                                        </div>
                                        <div className="col-span-2 text-right py-1 font-bold text-gray-700">
                                            ${item.total.toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                                <div className="flex justify-end p-4 text-xl font-bold text-blue-900">
                                    Total: ${newQuote.total_amount.toLocaleString()}
                                </div>
                            </div>

                            <div className="flex justify-end gap-4">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-8 py-3 text-gray-500 font-bold">Cancel</button>
                                <button type="submit" className="px-10 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700">Create Quote</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuotesManagement;
